import { useState, useMemo, useEffect } from "react"
import { X, Copy, ArrowLeftRight, Plane } from "lucide-react"

import Input from "../../../../components/ui/inputs/input"
import Select from "../../../../components/ui/select/select"
import Segmented from "../../../../components/ui/segmented/segmented"
import Button from "../../../../components/ui/button/button"
import Datepicker from "../../../../components/ui/datepicker/datepicker"
import Checkbox from "../../../../components/ui/checkbox/checkbox"
import SearchSelect from "../../../../components/ui/select/search-select"
import { printCurrencySymbol } from "../../../quoting/utils/functions"

import CARRIERS from "./data/carriers.json"
import AIRPORTS from "./data/airports.json"

// Full label map — keeps `airport_golf` so any transfer already saved with it
// still renders its label, even though it's no longer offered as a new choice.
const TRANSFER_TYPES = { airport: "Airport", golf: "Golf", airport_golf: "Airport & golf", resort_shuttle: "Resort Shuttles" }
// The choices offered in the Type picker (airport_golf retired).
const TRANSFER_TYPE_OPTIONS = { airport: "Airport", golf: "Golf", resort_shuttle: "Resort Shuttles" }
const CURRENCY_OPTIONS = { gbp: "GBP (£)", eur: "EUR (€)", usd: "USD ($)" }
const PRICE_MODE_OPTIONS = { per_person: "Per person", total: "Total" }

// Per-leg luggage options (multi-select). Exported so the itinerary card can
// render the chosen labels.
export const LUGGAGE_OPTIONS = {
	cabin: "Cabin bag",
	hold_20: "20kg hold bag",
	hold_23: "23kg hold bag",
	golf: "Golf clubs",
}

const toggleLuggage = (current = [], key) => (current.includes(key) ? current.filter((value) => value !== key) : [...current, key])
const toggleCourseUid = (current = [], uid) => (current.includes(uid) ? current.filter((value) => value !== uid) : [...current, uid])

// Carriers sorted alphabetically by name for the dropdown.
const CARRIERS_SORTED = Object.fromEntries(Object.entries(CARRIERS).sort((a, b) => a[1].localeCompare(b[1])))

// Airport fields read code-first: "LTN · London Luton Airport" (country dropped).
const airportDisplay = (code) => (code ? `${code} · ${(AIRPORTS[code] || code).split(",")[0]}` : "")
const carrierDisplay = (code, name) => (code ? `${name || CARRIERS[code] || code} (${code})` : "")

// Airline logo by IATA code (kiwi's public logo CDN), matching the itinerary card.
const carrierLogoUrl = (code) => (code ? `https://images.kiwi.com/airlines/64/${code.trim().toUpperCase()}.png` : "")

/**
 * Small airline logo for the carrier dropdown rows; falls back to a plane icon
 * when there's no code or the logo fails to load.
 */
function CarrierLogo({ code }) {
	const [failed, setFailed] = useState(false)
	const url = carrierLogoUrl(code)
	if (!url || failed) {
		return (
			<span className="flight-carrier-logo is-fallback">
				<Plane size={13} />
			</span>
		)
	}
	return <img className="flight-carrier-logo" src={url} alt="" onError={() => setFailed(true)} />
}

/**
 * Searchable picker that keeps the chosen label visible. Wraps SearchSelect
 * (which is otherwise an uncontrolled typeahead) so the field shows the current
 * selection and reflects what's typed.
 *
 * NB: SearchSelect seeds its displayed value from `defaultSelected` on mount
 * only, so a programmatic change (e.g. the "copy from outbound" buttons) won't
 * show unless the field remounts. Callers pass a `key` derived from the current
 * value so a copy re-seeds the field. `optionPrefix(optionKey)` renders an icon
 * (e.g. the airline logo) before each dropdown row.
 */
function PickerField({ options, label, placeholder, display, selectedKey, optionPrefix, showLabel = true, onPick }) {
	const [typed, setTyped] = useState("")

	// Pre-filter + cap so a huge list (airports) doesn't render thousands of
	// rows. Small lists (carriers) show in full before typing. The field stays
	// uncontrolled (matching the /quoting flight search) so typing works; the
	// current selection is shown via the note.
	const visible = useMemo(() => {
		const keys = Object.keys(options)
		const q = typed.trim().toUpperCase()
		if (!q) return keys.length <= 60 ? options : {}
		const out = {}
		let count = 0
		for (const key of keys) {
			if (count >= 50) break
			if (key.toUpperCase().includes(q) || (options[key] || "").toUpperCase().includes(q)) {
				out[key] = options[key]
				count += 1
			}
		}
		return out
	}, [typed, options])

	return (
		<SearchSelect
			options={visible}
			withKey
			externalFilter
			showLabel={showLabel}
			showClear
			hideArrow
			label={label}
			placeholder={display || placeholder}
			defaultSelected={display ? { option: selectedKey || display, value: display } : undefined}
			optionPrefix={optionPrefix}
			onTextChange={setTyped}
			onSelect={({ option, value }) => onPick(option, value)}
			clearInput={() => onPick("", "")}
		/>
	)
}

/**
 * Currency (GBP/EUR/USD) + per-person/total price entry for the transfer form
 * (the flight form lays the same fields out as a horizontal strip).
 */
function PriceFields({ item, onUpdate }) {
	const currency = item.currency || "gbp"
	const mode = item.priceMode || "per_person"

	return (
		<>
			<div className="flight-form-row">
				<Select label="Currency" showLabel value={CURRENCY_OPTIONS[currency]} selected={currency} options={CURRENCY_OPTIONS} onSelect={({ option }) => onUpdate(item.uid, { currency: option })} />
				<Select label="Price type" showLabel value={PRICE_MODE_OPTIONS[mode]} selected={mode} options={PRICE_MODE_OPTIONS} onSelect={({ option }) => onUpdate(item.uid, { priceMode: option })} />
			</div>
			<Input
				type="number"
				label={`Net cost (${mode === "total" ? "total" : "per person"})`}
				showLabel
				symbol={printCurrencySymbol(currency)}
				placeholder="Net cost"
				value={item.price}
				onChange={(value) => onUpdate(item.uid, { price: value })}
			/>
		</>
	)
}

/**
 * Course rows for a golf transfer: every round already in the quote, each with a
 * checkbox. Checking a course reveals a compact inline cost input (matching the
 * quantity/sleeps controls) plus a small per-person/total picker, so one golf
 * transfer covers - and prices - several courses in one go.
 *
 * Stored as `courseUids` (the checked rounds) + `coursePrices` keyed by round
 * uid: { price, priceMode }. Legacy single-course golf transfers (courseUid +
 * item-level price) seed the matching row so reopening one migrates it cleanly.
 */
function GolfCourseRows({ item, courses, onUpdate }) {
	const currency = item.currency || "gbp"
	// Checked rounds: the new multi-course shape, falling back to the legacy
	// single courseUid so old transfers open with their course ticked.
	const checkedUids = Array.isArray(item.courseUids) && item.courseUids.length ? item.courseUids : item.courseUid ? [item.courseUid] : []
	const coursePrices = item.coursePrices || {}

	// Price entry for a row: its own saved pricing, else (legacy) the item-level
	// price when this row is the old single courseUid.
	const pricingFor = (uid) => coursePrices[uid] || (uid === item.courseUid && item.price ? { price: item.price, priceMode: item.priceMode || "per_person" } : {})

	const toggleCourse = (uid) => {
		const next = checkedUids.includes(uid) ? checkedUids.filter((value) => value !== uid) : [...checkedUids, uid]
		const nextPrices = { ...coursePrices }
		if (!next.includes(uid)) delete nextPrices[uid]
		else if (!nextPrices[uid]) nextPrices[uid] = { price: pricingFor(uid).price || "", priceMode: pricingFor(uid).priceMode || "per_person" }
		// Interacting moves the item onto the multi-course shape for good - the
		// legacy single-course fields are cleared so they can't disagree.
		onUpdate(item.uid, { courseUids: next, coursePrices: nextPrices, courseUid: "", price: "" })
	}

	const setCoursePrice = (uid, patch) => {
		const current = pricingFor(uid)
		onUpdate(item.uid, {
			coursePrices: { ...coursePrices, [uid]: { price: current.price || "", priceMode: current.priceMode || "per_person", ...patch } },
			// Same migration as toggling: pricing a row supersedes the legacy fields.
			...(item.courseUid ? { courseUids: checkedUids, courseUid: "", price: "" } : {}),
		})
	}

	return (
		<div className="transfer-golf-courses">
			<span className="transfer-route-sublabel">Courses with transfers</span>
			<div className="transfer-golf-course-list">
				{courses.map((course) => {
					const checked = checkedUids.includes(course.uid)
					const pricing = pricingFor(course.uid)
					const mode = pricing.priceMode || "per_person"
					return (
						<div key={course.uid} className={["transfer-golf-course", checked ? "is-on" : ""].join(" ").trim()}>
							<Checkbox label={course.title} checked={checked} onClick={() => toggleCourse(course.uid)} />
							{checked && (
								<div className="transfer-golf-course-controls" onClick={(event) => event.stopPropagation()}>
									<Input
										wrapperClass="new-quote-inline-price"
										type="number"
										symbol={printCurrencySymbol(currency)}
										placeholder="0"
										value={pricing.price || ""}
										onChange={(value) => setCoursePrice(course.uid, { price: value })}
									/>
									<Select
										className="transfer-golf-mode"
										options={PRICE_MODE_OPTIONS}
										selected={mode}
										value={PRICE_MODE_OPTIONS[mode]}
										onSelect={({ option }) => setCoursePrice(course.uid, { priceMode: option })}
									/>
								</div>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}

/**
 * One flight leg on a single labelled row: carrier, From, To, Departs, Arrives -
 * with the per-leg luggage as a checkbox row beneath. Airports read code-first;
 * carriers show their logo in the dropdown; times are 24-hour. On a return trip
 * the return leg gets "Same airline" / "Reverse route" secondary buttons to fill
 * it from the outbound in a click.
 */
function FlightLegRow({ item, leg, index, legCount, tripType, defaultLegMillis, onSetLeg }) {
	const label = tripType === "oneway" ? "Flight" : index === 0 ? "Outbound" : "Return"

	const defaultMillis = defaultLegMillis ? defaultLegMillis(index) : 0
	const departOpenOn = defaultMillis ? new Date(defaultMillis) : undefined
	const arriveOpenOn = leg.depMillis ? new Date(leg.depMillis) : departOpenOn

	const isReturnLeg = tripType === "return" && legCount > 1 && index === legCount - 1
	const outbound = item.legs[0] || {}
	const canCopyCarrier = isReturnLeg && Boolean(outbound.carrierCode || outbound.carrier)
	const canCopyAirports = isReturnLeg && Boolean(outbound.fromAirport || outbound.toAirport)

	return (
		<div className="flight-leg">
			<div className="flight-leg-head">
				<span className="flight-leg-tag">{label}</span>
				{(canCopyCarrier || canCopyAirports) && (
					<div className="flight-leg-copy">
						{canCopyCarrier && (
							<Button
								type="secondary"
								xsSmall
								label="Same airline"
								icon={<Copy size={13} />}
								onClick={() => onSetLeg(item.uid, index, { carrier: outbound.carrier, carrierCode: outbound.carrierCode })}
							/>
						)}
						{canCopyAirports && (
							<Button
								type="secondary"
								xsSmall
								label="Reverse route"
								icon={<ArrowLeftRight size={13} />}
								onClick={() => onSetLeg(item.uid, index, { fromAirport: outbound.toAirport, toAirport: outbound.fromAirport })}
							/>
						)}
					</div>
				)}
			</div>

			<div className="flight-leg-grid">
				<div className="flight-leg-cell">
					<span className="flight-leg-label">Carrier</span>
					<PickerField
						key={`carrier-${index}-${leg.carrierCode || "none"}`}
						label="Carrier"
						placeholder="Airline name or code"
						showLabel={false}
						options={CARRIERS_SORTED}
						display={carrierDisplay(leg.carrierCode, leg.carrier)}
						selectedKey={leg.carrierCode}
						optionPrefix={(code) => <CarrierLogo code={code} />}
						onPick={(code) => onSetLeg(item.uid, index, { carrierCode: code, carrier: code ? CARRIERS[code] || code : "" })}
					/>
				</div>

				<div className="flight-leg-cell">
					<span className="flight-leg-label">From</span>
					<PickerField
						key={`from-${index}-${leg.fromAirport || "none"}`}
						label="From"
						placeholder="Airport code or name"
						showLabel={false}
						options={AIRPORTS}
						display={airportDisplay(leg.fromAirport)}
						selectedKey={leg.fromAirport}
						onPick={(code) => onSetLeg(item.uid, index, { fromAirport: code })}
					/>
				</div>

				<div className="flight-leg-cell">
					<span className="flight-leg-label">To</span>
					<PickerField
						key={`to-${index}-${leg.toAirport || "none"}`}
						label="To"
						placeholder="Airport code or name"
						showLabel={false}
						options={AIRPORTS}
						display={airportDisplay(leg.toAirport)}
						selectedKey={leg.toAirport}
						onPick={(code) => onSetLeg(item.uid, index, { toAirport: code })}
					/>
				</div>

				<div className="flight-leg-cell">
					<span className="flight-leg-label">Departs</span>
					<Datepicker placeholder="Departs" hasTime time24 openOn={departOpenOn} value={leg.depMillis} onSelect={(millis) => onSetLeg(item.uid, index, { depMillis: millis })} />
				</div>

				<div className="flight-leg-cell">
					<span className="flight-leg-label">Arrives</span>
					<Datepicker placeholder="Arrives" hasTime time24 openOn={arriveOpenOn} value={leg.arrMillis} onSelect={(millis) => onSetLeg(item.uid, index, { arrMillis: millis })} />
				</div>
			</div>

			<div className="flight-leg-luggage">
				<span className="flight-leg-label">Luggage included</span>
				<div className="flight-leg-luggage-row">
					{Object.entries(LUGGAGE_OPTIONS).map(([key, optionLabel]) => (
						<Checkbox key={key} label={optionLabel} checked={(leg.luggage || []).includes(key)} onClick={() => onSetLeg(item.uid, index, { luggage: toggleLuggage(leg.luggage, key) })} />
					))}
				</div>
			</div>
		</div>
	)
}

// A Euro Tunnel crossing is priced in pounds or euros only.
const TUNNEL_CURRENCY_OPTIONS = { gbp: "GBP (£)", eur: "EUR (€)" }

/**
 * Edit modal for a flight, transfer or Euro Tunnel basket item, so the heavier
 * form lives here rather than expanding down the left column. Carriers +
 * airports are picked from searchable dropdowns.
 */
export default function FlightTransferModal({ item, hotels = [], courses = [], defaultLegMillis, onUpdate, onSetTripType, onSetLeg, onClose }) {
	const isFlight = item.type === "flight"
	const isEuroTunnel = item.type === "eurotunnel"

	const flightCurrency = item.currency || "gbp"
	const flightMode = item.priceMode || "per_person"

	const hotelOptions = hotels.reduce((options, hotel) => ({ ...options, [hotel.uid]: hotel.title }), {})
	const courseOptions = courses.reduce((options, course) => ({ ...options, [course.uid]: course.title }), {})

	const needsAirport = item.transferType === "airport" || item.transferType === "airport_golf"
	const needsCourse = item.transferType === "airport_golf"

	// With exactly one hotel on the quote there's no choice to make, so a transfer
	// that hasn't picked a hotel defaults to it.
	const onlyHotelUid = hotels.length === 1 ? hotels[0].uid : ""
	useEffect(() => {
		if (item.type === "transfer" && !item.hotelUid && onlyHotelUid) onUpdate(item.uid, { hotelUid: onlyHotelUid })
		// Re-checked when the transfer or hotel list changes; onUpdate is stable
		// enough for this (a basket setState wrapper).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [item.type, item.uid, item.hotelUid, onlyHotelUid])

	// Transfer endpoints laid out left-to-right in travel order (airport -> hotel
	// -> course) with arrows between, so the "from -> to" route reads clearly. The
	// transfers themselves are return trips; the arrows are presentational.
	const airportField = (
		<div className="transfer-route-field" key="airport">
			<PickerField
				key={`transfer-airport-${item.airport || "none"}`}
				label="Airport"
				placeholder="Search airport (e.g. Malaga / AGP)"
				options={AIRPORTS}
				display={airportDisplay(item.airport)}
				selectedKey={item.airport}
				onPick={(code, value) => onUpdate(item.uid, { airport: code, airportLabel: value })}
			/>
		</div>
	)
	const hotelField = (
		<div className="transfer-route-field" key="hotel">
			{hotels.length === 0 ? (
				<small className="new-quote-rooms-empty">Add a hotel first to link this transfer.</small>
			) : (
				<Select label="Hotel" showLabel value={hotelOptions[item.hotelUid]} selected={item.hotelUid} options={hotelOptions} placeholder="Choose hotel" onSelect={({ option }) => onUpdate(item.uid, { hotelUid: option })} />
			)}
		</div>
	)
	const courseField = (
		<div className="transfer-route-field" key="course">
			{courses.length === 0 ? (
				<small className="new-quote-rooms-empty">Add a golf round first to link this transfer.</small>
			) : (
				<Select label="Golf course" showLabel value={courseOptions[item.courseUid]} selected={item.courseUid} options={courseOptions} placeholder="Choose course" onSelect={({ option }) => onUpdate(item.uid, { courseUid: option })} />
			)}
		</div>
	)

	const routeFields = []
	if (needsAirport) routeFields.push(airportField)
	routeFields.push(hotelField)
	if (needsCourse) routeFields.push(courseField)

	// Interleave arrow separators between the endpoint fields (each already
	// keyed) so the rendered list is a flat keyed array - no Fragment wrapper.
	const routeNodes = []
	routeFields.forEach((field, index) => {
		routeNodes.push(field)
		if (index < routeFields.length - 1) {
			routeNodes.push(
				<span className="transfer-route-arrow" aria-hidden="true" key={`arrow-${index}`}>
					<ArrowLeftRight size={15} />
				</span>,
			)
		}
	})

	return (
		<div className="package-modal-overlay is-form-modal">
			<div className={["package-modal flight-modal", isFlight ? "is-flight" : ""].join(" ").trim()} role="dialog" aria-modal="true" aria-label={isFlight ? "Edit flight" : isEuroTunnel ? "Edit Euro Tunnel" : "Edit transfer"}>
				<div className="package-modal-header">
					<div>
						<h3>{isFlight ? "Flight details" : isEuroTunnel ? "Euro Tunnel" : "Transfer details"}</h3>
					</div>
					<button type="button" className="package-modal-close" onClick={onClose} aria-label="Close">
						<X size={18} />
					</button>
				</div>

				<div className="package-modal-body">
					{isEuroTunnel ? (
						// Like a flight: one-way or return, with a crossing datetime per
						// direction (these place it on the timeline). Priced per booking,
						// so just one net price to us in pounds or euros.
						<div className="flight-form">
							<Segmented
								className="flight-trip"
								options={{ return: "Return", oneway: "One way" }}
								selected={item.tripType || "return"}
								onSelect={({ option }) => onUpdate(item.uid, { tripType: option })}
							/>

							<div className="flight-form-row">
								<Datepicker
									label="Outbound crossing"
									showLabel
									placeholder="Outbound crossing"
									hasTime
									time24
									openOn={defaultLegMillis && defaultLegMillis(0) ? new Date(defaultLegMillis(0)) : undefined}
									value={item.outboundMillis}
									onSelect={(millis) => onUpdate(item.uid, { outboundMillis: millis })}
								/>
								{(item.tripType || "return") === "return" && (
									<Datepicker
										label="Return crossing"
										showLabel
										placeholder="Return crossing"
										hasTime
										time24
										openOn={defaultLegMillis && defaultLegMillis(1) ? new Date(defaultLegMillis(1)) : undefined}
										value={item.returnMillis}
										onSelect={(millis) => onUpdate(item.uid, { returnMillis: millis })}
									/>
								)}
							</div>

							<div className="flight-form-row">
								<Select
									label="Currency"
									showLabel
									value={TUNNEL_CURRENCY_OPTIONS[item.currency || "gbp"]}
									selected={item.currency || "gbp"}
									options={TUNNEL_CURRENCY_OPTIONS}
									onSelect={({ option }) => onUpdate(item.uid, { currency: option })}
								/>
								<Input
									type="number"
									label="Net cost (total)"
									showLabel
									symbol={printCurrencySymbol(item.currency || "gbp")}
									placeholder="Net cost"
									value={item.price}
									onChange={(value) => onUpdate(item.uid, { price: value })}
								/>
							</div>
						</div>
					) : isFlight ? (
						<div className="flight-form">
							<Segmented
								className="flight-trip"
								options={{ return: "Return", oneway: "One way" }}
								selected={item.tripType || "return"}
								onSelect={({ option }) => onSetTripType(item.uid, option)}
							/>

							{item.legs.map((leg, index) => (
								<FlightLegRow
									key={index}
									item={item}
									leg={leg}
									index={index}
									legCount={item.legs.length}
									tripType={item.tripType}
									defaultLegMillis={defaultLegMillis}
									onSetLeg={onSetLeg}
								/>
							))}

							<div className="flight-pricing">
								<Select label="Currency" showLabel value={CURRENCY_OPTIONS[flightCurrency]} selected={flightCurrency} options={CURRENCY_OPTIONS} onSelect={({ option }) => onUpdate(item.uid, { currency: option })} />
								<Select label="Price type" showLabel value={PRICE_MODE_OPTIONS[flightMode]} selected={flightMode} options={PRICE_MODE_OPTIONS} onSelect={({ option }) => onUpdate(item.uid, { priceMode: option })} />
								<Input
									type="number"
									label="Net cost"
									showLabel
									symbol={printCurrencySymbol(flightCurrency)}
									placeholder="Net cost"
									value={item.price}
									onChange={(value) => onUpdate(item.uid, { price: value })}
								/>
								<Checkbox
									label="Add flight cost to deposit"
									checked={Boolean(item.chargeFullFlight)}
									onClick={() => onUpdate(item.uid, { chargeFullFlight: !item.chargeFullFlight })}
								/>
							</div>
						</div>
					) : (
						<div className="flight-form">
							<Select label="Type" showLabel value={TRANSFER_TYPES[item.transferType]} selected={item.transferType} options={TRANSFER_TYPE_OPTIONS} onSelect={({ option }) => onUpdate(item.uid, { transferType: option })} />

							{item.transferType === "resort_shuttle" ? (
								// One shuttle linking a hotel to several courses in the resort,
								// so the agent doesn't add a separate golf transfer per course.
								<div className="transfer-route">
									<span className="transfer-route-label">From hotel to courses</span>
									{hotels.length === 0 ? (
										<small className="new-quote-rooms-empty">Add a hotel first to link this shuttle.</small>
									) : (
										<Select label="Hotel" showLabel value={hotelOptions[item.hotelUid]} selected={item.hotelUid} options={hotelOptions} placeholder="Choose hotel" onSelect={({ option }) => onUpdate(item.uid, { hotelUid: option })} />
									)}
									{courses.length === 0 ? (
										<small className="new-quote-rooms-empty">Add golf rounds first to choose which have shuttles.</small>
									) : (
										<div className="transfer-shuttle-courses">
											<span className="transfer-route-sublabel">Courses with shuttles</span>
											<div className="transfer-shuttle-course-list">
												{courses.map((course) => (
													<Checkbox
														key={course.uid}
														label={course.title}
														checked={(item.courseUids || []).includes(course.uid)}
														onClick={() => onUpdate(item.uid, { courseUids: toggleCourseUid(item.courseUids, course.uid) })}
													/>
												))}
											</div>
										</div>
									)}
								</div>
							) : item.transferType === "golf" ? (
								// One golf transfer covering every course that needs it: tick the
								// courses and price each inline (per person or total), instead of
								// adding a separate transfer per course.
								<div className="transfer-route">
									<span className="transfer-route-label">From hotel to courses</span>
									{hotels.length === 0 ? (
										<small className="new-quote-rooms-empty">Add a hotel first to link this transfer.</small>
									) : (
										<Select label="Hotel" showLabel value={hotelOptions[item.hotelUid]} selected={item.hotelUid} options={hotelOptions} placeholder="Choose hotel" onSelect={({ option }) => onUpdate(item.uid, { hotelUid: option })} />
									)}
									{courses.length === 0 ? (
										<small className="new-quote-rooms-empty">Add golf rounds first to choose which need this transfer.</small>
									) : (
										<GolfCourseRows item={item} courses={courses} onUpdate={onUpdate} />
									)}
								</div>
							) : (
								<div className="transfer-route">
									<span className="transfer-route-label">Route</span>
									<div className="transfer-route-fields">{routeNodes}</div>
								</div>
							)}

							{/* Golf transfers price per course above, so the shared price fields
							    drop to just the currency (it applies to every course line). */}
							{item.transferType === "golf" ? (
								<div className="flight-form-row">
									<Select label="Currency" showLabel value={CURRENCY_OPTIONS[item.currency || "gbp"]} selected={item.currency || "gbp"} options={CURRENCY_OPTIONS} onSelect={({ option }) => onUpdate(item.uid, { currency: option })} />
								</div>
							) : (
								<PriceFields item={item} onUpdate={onUpdate} />
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
